/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { shuffle } from './shuffle';
import { useTimedCallback } from './use-timed-callback';

const TYPE_SPEED_MS = 38;
const TYPE_PAUSE_MS = 2500;
const DELETE_SPEED_MS = 0;
const DELETE_PAUSE_MS = 500;

/**
 * We have 3 buckets of copies.
 * - 1: 1 copy
 * - 2: 4 copies
 * - 3: 6 copies
 *
 * The algorithm is as follows:
 * - We show the first copy for a longer period of time.
 * - Then we show a random copy from bucket 2
 * - Finally we show all copies from bucket 3 in random order.
 *
 * In the next round we will pick a different random copy from bucket 2 and again show all copies from bucket 3 in a new random order.
 *
 * Example:
 * Round 1: 1 - 2B - 3B 3A 3E 3C 3F 3D
 * Round 2: 1 - 2C - 3A 3E 3B 3C 3F 3D
 * Round 3: 1 - 2A - 3F 3D 3C 3B 3E 3A
 * Round 4: 1 - 2D - 3E 3A 3F 3B 3C 3D
 * Round 5: 1 - 2B - 3C 3D 3A 3E 3F 3B
 * ...
 */
const PLACEHOLDER_BUCKETS = [
  // We show this copy first for an extended period of time
  ['SEARCH_AND_SWIPE_SEARCH_WITH_AI'],
  // We shuffle the first bucket here, as we only need one shuffle
  shuffle([
    'JOBS_SEARCH_AI_INPUT_PLACEHOLDER_1',
    'JOBS_SEARCH_AI_INPUT_PLACEHOLDER_2',
    'JOBS_SEARCH_AI_INPUT_PLACEHOLDER_3',
    'JOBS_SEARCH_AI_INPUT_PLACEHOLDER_4',
  ]),
  [
    'JOBS_SEARCH_AI_INPUT_PLACEHOLDER_5',
    'JOBS_SEARCH_AI_INPUT_PLACEHOLDER_6',
    'JOBS_SEARCH_AI_INPUT_PLACEHOLDER_7',
    'JOBS_SEARCH_AI_INPUT_PLACEHOLDER_8',
    'JOBS_SEARCH_AI_INPUT_PLACEHOLDER_9',
    'JOBS_SEARCH_AI_INPUT_PLACEHOLDER_10',
  ],
];

type UseAnimatedPlaceholderOptions = {
  disable?: boolean;
};

export const useAnimatedPlaceholder = (
  ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement>,
  { disable }: UseAnimatedPlaceholderOptions = {}
) => {
  const { formatMessage } = useIntl();
  const placeholdersRef = useRef(
    PLACEHOLDER_BUCKETS.map((bucket) =>
      bucket.map((id) => formatMessage({ id }))
    )
  );
  const bucketRef = useRef(0);
  const indexRef = useRef([0, -1, 0]);
  const charIndexRef = useRef(0);
  const { run, cancel } = useTimedCallback({ speed: TYPE_SPEED_MS });

  const getNextOptions = (delay?: number) => ({
    delay,
    cancel: Boolean(ref.current?.value),
  });

  const type = () => {
    // If there is a value, we cut the setTimeout chain
    if (ref.current?.value || disable) return;

    const bucket = placeholdersRef.current[bucketRef.current];
    const index = indexRef.current[bucketRef.current];
    const placeholder = bucket[index];
    const newPlaceholder = placeholder.slice(0, charIndexRef.current);
    ref.current?.setAttribute('placeholder', newPlaceholder);

    if (charIndexRef.current < placeholder.length) {
      ++charIndexRef.current;
      run(type);
    } else {
      charIndexRef.current = 0;
      run(
        remove(placeholder),
        getNextOptions(
          bucketRef.current === 0 ? TYPE_PAUSE_MS * 2 : TYPE_PAUSE_MS
        )
      );
    }
  };

  const remove = (placeholder: string) => () => {
    // If there is a value, we cut the setTimeout chain
    if (ref.current?.value) return;

    const newPlaceholder = placeholder.slice(0, -1);
    ref.current?.setAttribute('placeholder', newPlaceholder);

    if (newPlaceholder.length > 0) {
      run(remove(newPlaceholder), getNextOptions(DELETE_SPEED_MS));
    } else {
      if (bucketRef.current === 0) {
        bucketRef.current = 1;
        indexRef.current[1] =
          (indexRef.current[1] + 1) % placeholdersRef.current[1].length;
      } else if (bucketRef.current === 1) {
        bucketRef.current = 2;
        indexRef.current[2] = 0;
        // Third bucket is shuffled here, every time we switch to it
        placeholdersRef.current[2] = shuffle(placeholdersRef.current[2]);
      } else {
        if (indexRef.current[2] === placeholdersRef.current[2].length - 1) {
          bucketRef.current = 0;
        } else {
          indexRef.current[2] =
            (indexRef.current[2] + 1) % placeholdersRef.current[2].length;
        }
      }

      run(type, getNextOptions(DELETE_PAUSE_MS));
    }
  };

  useEffect(() => {
    const shouldDisable =
      disable && (indexRef.current !== null || charIndexRef.current !== null);
    if (shouldDisable) {
      cancel();
    } else if (ref.current?.placeholder) {
      type();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disable]);

  return type;
};
